import {
  createPlaneFrame,
  createRoofPackingFrame,
  makeFrame,
  type LatLng,
  type Vec2,
} from '@payaca/helpers/solarFacePacker';

export type SnapPanelDragArgs = {
  selectedPanelCorners: readonly (readonly LatLng[])[];
  /** Unselected panels on the dragged face only; the caller filters by face. */
  stationaryPanelCorners: readonly (readonly LatLng[])[];
  /** Translation from the original drag-start corners, not the last preview. */
  delta: LatLng;
  panelGapM: number;
  columnGapM?: number;
  rowGapM?: number;
  /** Flush arrays snap in their physical roof plane; frames snap in plan. */
  roofPlane?: { pitchDegrees: number; azimuthDegrees: number };
  /** Applied array downslope bearing, never inferred from ring start/winding. */
  /** Null explicitly disables snapping when applied axes could not be retained. */
  arrayAzimuthDegrees?: number | null;
  snapThresholdM: number;
};

const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.y * b.y;
const subtract = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const project = (ring: readonly Vec2[], axis: Vec2) => {
  const values = ring.map((point) => dot(point, axis));
  return { min: Math.min(...values), max: Math.max(...values) };
};

// Separating axes handle blockers of any orientation, including incompatible
// neighbours which must not provide snap targets. Touching edges are allowed.
const overlaps = (a: readonly Vec2[], b: readonly Vec2[]) =>
  [a, b].every((ring) =>
    ring.every((point, index) => {
      const edge = subtract(ring[(index + 1) % ring.length], point);
      const length = Math.hypot(edge.x, edge.y);
      if (length < 1e-8) return true;
      const axis = { x: -edge.y / length, y: edge.x / length };
      const pa = project(a, axis);
      const pb = project(b, axis);
      return Math.min(pa.max, pb.max) - Math.max(pa.min, pb.min) > 1e-6;
    })
  );

/**
 * Magnetise a rigid selection to existing rows, columns and adjacent panel
 * gaps without repacking it. Applied bearing establishes column/row axes;
 * corner start and winding carry no axis meaning. Gaps use the packing plane,
 * while the attraction threshold stays in ground metres. Without a bearing,
 * legacy equal-gap snapping can use either edge; unequal gaps remain unsnapped.
 * An obstructed snap falls back to the raw drag, whose validity is the caller's
 * responsibility. Only actual anchor lines are used, never an infinite grid.
 */
export const snapPanelDrag = ({
  selectedPanelCorners,
  stationaryPanelCorners,
  delta,
  panelGapM,
  columnGapM = panelGapM,
  rowGapM = panelGapM,
  roofPlane,
  arrayAzimuthDegrees,
  snapThresholdM,
}: SnapPanelDragArgs): LatLng => {
  const origin = selectedPanelCorners[0]?.[0];
  if (
    !origin ||
    stationaryPanelCorners.length === 0 ||
    !Number.isFinite(snapThresholdM) ||
    snapThresholdM <= 0 ||
    !Number.isFinite(columnGapM) ||
    !Number.isFinite(rowGapM) ||
    columnGapM < 0 ||
    rowGapM < 0 ||
    arrayAzimuthDegrees === null ||
    (arrayAzimuthDegrees == null && columnGapM !== rowGapM) ||
    (arrayAzimuthDegrees != null && !Number.isFinite(arrayAzimuthDegrees)) ||
    (roofPlane != null &&
      (!Number.isFinite(roofPlane.pitchDegrees) ||
        roofPlane.pitchDegrees < 0 ||
        roofPlane.pitchDegrees >= 90 ||
        !Number.isFinite(roofPlane.azimuthDegrees)))
  )
    return delta;

  const frame = makeFrame(origin);
  const plane =
    arrayAzimuthDegrees != null
      ? createRoofPackingFrame(
          { x: 0, y: 0 },
          roofPlane?.pitchDegrees ?? 0,
          roofPlane?.azimuthDegrees ?? arrayAzimuthDegrees,
          arrayAzimuthDegrees
        )
      : createPlaneFrame(
          { x: 0, y: 0 },
          roofPlane?.pitchDegrees ?? 0,
          roofPlane?.azimuthDegrees ?? 0
        );
  const toPlane = (point: LatLng) => plane.toPlane(frame.toM(point));
  const selected = selectedPanelCorners.map((ring) => ring.map(toPlane));
  const stationary = stationaryPanelCorners.map((ring) => ring.map(toPlane));
  const raw = toPlane({
    lat: origin.lat + delta.lat,
    lng: origin.lng + delta.lng,
  });
  const axisGaps = [columnGapM, rowGapM];
  const planeThresholdM =
    snapThresholdM / Math.cos(((roofPlane?.pitchDegrees ?? 0) * Math.PI) / 180);
  const candidates: Array<{
    correction: Vec2;
    axes: number;
    distance: number;
  }> = [];

  for (const panel of selected) {
    if (panel.length !== 4) continue;
    const edge = subtract(panel[1], panel[0]);
    const length = Math.hypot(edge.x, edge.y);
    if (length < 1e-6) continue;
    const u =
      arrayAzimuthDegrees != null
        ? { x: 1, y: 0 }
        : { x: edge.x / length, y: edge.y / length };
    const v =
      arrayAzimuthDegrees != null ? { x: 0, y: 1 } : { x: -u.y, y: u.x };
    const aligned = (ring: readonly Vec2[]) =>
      ring.every((point, index) => {
        const side = subtract(ring[(index + 1) % ring.length], point);
        const size = Math.hypot(side.x, side.y);
        return (
          size > 1e-6 &&
          Math.min(Math.abs(dot(side, u)), Math.abs(dot(side, v))) / size <
            Math.sin(Math.PI / 180)
        );
      });
    if (!aligned(panel)) continue;
    const bounds = [project(panel, u), project(panel, v)];

    for (const anchor of stationary) {
      if (anchor.length !== 4) continue;
      // All edges must follow this footprint's axes (within one degree).
      if (!aligned(anchor)) continue;
      const targets = [project(anchor, u), project(anchor, v)];
      if (
        !bounds.every((bound, index) => {
          const size = bound.max - bound.min;
          const targetSize = targets[index].max - targets[index].min;
          return size > 1e-6 && Math.abs(size - targetSize) <= size * 0.01;
        })
      )
        continue;

      // A shared row or column alone is not a nearby array: measure the
      // footprint separation at the raw drag position before offering guides.
      const separations = [u, v].map((axis, index) => {
        const shift = dot(raw, axis);
        const bound = bounds[index];
        const target = targets[index];
        return Math.max(
          0,
          target.min - (bound.max + shift),
          bound.min + shift - target.max
        );
      });
      if (
        Math.hypot(
          ...separations.map(
            (distance, index) => distance / (axisGaps[index] + planeThresholdM)
          )
        ) > 1
      )
        continue;

      const corrections = [u, v].map((axis, index) => {
        const bound = bounds[index];
        const target = targets[index];
        const shift = dot(raw, axis);
        return [
          (target.min + target.max - bound.min - bound.max) / 2 - shift,
          target.max + axisGaps[index] - bound.min - shift,
          target.min - axisGaps[index] - bound.max - shift,
        ].filter((value) => Math.abs(value) <= planeThresholdM);
      });
      const add = (x: number, y: number, axes: number) => {
        const correction = { x: u.x * x + v.x * y, y: u.y * x + v.y * y };
        const ground = plane.toGround(correction);
        const distance = Math.hypot(ground.x, ground.y);
        if (distance > snapThresholdM) return;
        candidates.push({
          correction,
          axes,
          distance,
        });
      };
      for (const x of corrections[0]) {
        add(x, 0, 1);
        for (const y of corrections[1]) add(x, y, 2);
      }
      for (const y of corrections[1]) add(0, y, 1);
    }
  }

  // Prefer a complete grid alignment over a single-axis guide, then the
  // smallest correction. Stable sorting keeps ties deterministic.
  candidates.sort((a, b) => b.axes - a.axes || a.distance - b.distance);
  for (const { correction } of candidates) {
    const translation = { x: raw.x + correction.x, y: raw.y + correction.y };
    const blocked = selected.some((ring) => {
      const moved = ring.map((point) => ({
        x: point.x + translation.x,
        y: point.y + translation.y,
      }));
      return stationary.some(
        (anchor) => anchor.length >= 3 && overlaps(moved, anchor)
      );
    });
    if (blocked) continue;
    const snapped = frame.toLatLng(plane.toGround(translation));
    return { lat: snapped.lat - origin.lat, lng: snapped.lng - origin.lng };
  }
  return delta;
};
